import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuthStore } from '../lib/auth-store';
import { useT } from '../lib/i18n';
import { useFamily } from '../lib/landings';
import { EmptyState } from '../components/ui/EmptyState';
import {
  LandingPage,
  KPIRow,
  CardsRow,
  LandingSkeleton,
  LandingError,
  LandingGuard,
  KPICard,
  LandingCard,
  LandingCardLine,
  ModuleGatedCard,
} from '../components/portal-shell';
import { formatDate, formatINR, timeAgo } from '../lib/format';

export const Route = createFileRoute('/_authed/family')({
  component: FamilyPage,
});

function FamilyPage() {
  return (
    <LandingGuard landingPath="/family">
      <FamilyContent />
    </LandingGuard>
  );
}

function FamilyContent() {
  const { t } = useT();
  const user = useAuthStore((s) => s.user);
  const query = useFamily({ enabled: !!user });

  if (query.isLoading || (!query.data && !query.isError)) {
    return (
      <LandingPage
        title={t('family.title')}
        meta={user ? user.displayName : undefined}
        intro={t('family.intro')}
      >
        <LandingSkeleton />
      </LandingPage>
    );
  }
  if (query.isError || !query.data) {
    return (
      <LandingPage title={t('family.title')}>
        <LandingError error={query.error} onRetry={() => query.refetch()} />
      </LandingPage>
    );
  }

  const { data } = query.data;
  const childNames = data.children.map((c) => c.name).join(' · ');

  return (
    <LandingPage
      title={t('family.title')}
      meta={
        user
          ? `${user.displayName} · ${t('family.childCount', { count: data.children.length })}`
          : undefined
      }
      intro={t('family.intro')}
    >
      <KPIRow>
        <KPICard
          label={t('family.kpi.children')}
          value={data.children.length}
          footnote={childNames || t('family.kpi.noChildrenLinked')}
        />
        <KPICard
          label={t('family.kpi.feeDue')}
          value={formatINR(data.feeDueSummary.amountPaise)}
          footnote={
            data.feeDueSummary.dueDate
              ? t('family.kpi.dueOn', {
                  // Midday UTC, so the calendar day is the same in every time zone
                  // (midnight UTC is still the day before for a parent in the Americas).
                  date: formatDate(`${data.feeDueSummary.dueDate}T12:00:00Z`),
                })
              : t('family.kpi.nothingOutstanding')
          }
          tone={data.feeDueSummary.amountPaise > 0 ? 'warning' : 'success'}
        />
        <KPICard
          label={t('family.kpi.pendingFeedback')}
          value={data.pendingFeedbackCount ?? '—'}
          footnote={
            data.pendingFeedbackCount === null
              ? t('family.kpi.feedbackDisabled')
              : data.pendingFeedbackCount === 0
                ? t('family.kpi.caughtUp')
                : t('family.kpi.openBelow')
          }
          newFor="/me/feedback"
        />
        <KPICard
          label={t('family.kpi.wallet')}
          value={data.walletBalance ? formatINR(data.walletBalance.totalPaise) : '—'}
          footnote={
            data.walletBalance
              ? t('family.kpi.acrossAccounts', {
                  count: data.walletBalance.perChild.length,
                })
              : t('family.kpi.walletDisabled')
          }
          newFor="/me/referrals"
        />
      </KPIRow>

      <CardsRow>
        {data.children.length > 0 ? (
          data.children.map((child) => (
            <LandingCard
              key={child.studentId}
              title={child.name}
              titleAction={
                child.batchName ? (
                  <span className="text-[11px] text-[color:var(--color-text-muted)]">
                    {child.batchName}
                  </span>
                ) : null
              }
            >
              <LandingCardLine>
                {t('family.attendanceThisMonth')}{' '}
                <span className="tabular-nums font-medium">
                  {child.attendancePercent30d}%
                </span>
              </LandingCardLine>
              <LandingCardLine last>
                {child.latestRemark ? (
                  <>
                    {t('family.lastRemark')} <em>“{child.latestRemark.note}”</em>{' '}
                    <span className="text-[color:var(--color-text-muted)]">
                      · {timeAgo(child.latestRemark.occurredAt)}
                    </span>
                  </>
                ) : (
                  <span className="text-[color:var(--color-text-muted)]">{t('family.noRecentRemarks')}</span>
                )}
              </LandingCardLine>
            </LandingCard>
          ))
        ) : (
          <LandingCard title={t('family.noChildrenLinkedTitle')} className="md:col-span-2">
            <EmptyState
              title={t('family.addFirstChild')}
              description={t('family.contactOffice')}
              className="py-6"
            />
          </LandingCard>
        )}

        {data.recentMarks ? (
          data.recentMarks.length > 0 ? (
            <LandingCard title={t('family.recentMarks')} newFor="/me/marks">
              {data.recentMarks.slice(0, 5).map((m, idx, arr) => (
                <LandingCardLine
                  key={`${m.studentId}-${m.recordedAt}-${idx}`}
                  last={idx === arr.length - 1}
                >
                  <span className="font-medium">{m.studentName}</span> · {m.subject}{' '}
                  <span className="tabular-nums font-medium">
                    {m.score}/{m.maxScore}
                  </span>
                </LandingCardLine>
              ))}
            </LandingCard>
          ) : (
            <LandingCard title={t('family.recentMarks')} newFor="/me/marks">
              <EmptyState
                title={t('family.noMarksYet')}
                description={t('family.marksWillAppear')}
                className="py-6"
              />
            </LandingCard>
          )
        ) : (
          <ModuleGatedCard
            title={t('family.recentMarks')}
            description={t('family.examsDisabled')}
          />
        )}

        {data.referralCode ? (
          data.referralCode.length > 0 ? (
            <LandingCard title={t('family.referralCodes')} newFor="/me/referrals">
              {data.referralCode.map((r, idx, arr) => (
                <LandingCardLine key={r.studentId} last={idx === arr.length - 1}>
                  <span className="text-[color:var(--color-text-muted)]">{t('family.remarkFor')}</span>{' '}
                  <span className="font-medium">{nameFor(data.children, r.studentId, t('family.childFallback'))}</span>:{' '}
                  <code className="rounded bg-[color:var(--color-bg-elevated)] px-1 py-0.5 font-mono text-xs">
                    {r.code}
                  </code>
                </LandingCardLine>
              ))}
            </LandingCard>
          ) : (
            <LandingCard title={t('family.referralCodes')} newFor="/me/referrals">
              <EmptyState
                title={t('family.noCodesYet')}
                description={t('family.codesWillAppear')}
                className="py-6"
              />
            </LandingCard>
          )
        ) : (
          <ModuleGatedCard
            title={t('family.referralCodes')}
            description={t('family.referralsDisabled')}
          />
        )}
      </CardsRow>

      <p className="text-xs text-[color:var(--color-text-muted)]">
        {t('family.needHelp')}{' '}
        <Link to="/announcements" className="text-gold-700 dark:text-gold-300 underline">
          {t('family.openAnnouncements')}
        </Link>{' '}
        {t('family.forContactDetails')}
      </p>
    </LandingPage>
  );
}

/**
 * `fallback` is passed in rather than hardcoded: this returns user-visible text,
 * and the literal 'Child' that used to sit here rendered in English to a
 * Malayalam parent whenever a referral row pointed at a student missing from
 * the children list. The lint ratchet cannot see it — it is a JS string in a
 * helper, not JSX — so the only fix is not to have the literal.
 */
function nameFor(
  children: ReadonlyArray<{ studentId: string; name: string }>,
  id: string,
  fallback: string,
): string {
  return children.find((c) => c.studentId === id)?.name ?? fallback;
}
